import React, { useState } from "react";
import { X, Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/auth/AuthContext";

export default function LoginModal({ open, onClose }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !password) return;
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.name}`);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Login failed", { description: formatApiErrorDetail(detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="login-modal"
      className="fixed inset-0 z-50 overflow-y-auto bg-zinc-950/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="flex min-h-screen items-center justify-center p-4">
        <form
          onClick={(e) => e.stopPropagation()}
          onSubmit={submit}
          className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8 shadow-2xl"
        >
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#EFF4FF] text-[#002FA7]">
              <LogIn className="h-5 w-5" strokeWidth={1.8} />
            </span>
            <h3 className="font-serif text-2xl text-zinc-950">Log In</h3>
          </div>
          <button
            type="button"
            data-testid="login-close"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-900"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              Email
            </label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full h-11 rounded-md border border-zinc-300 bg-white px-3 font-sans text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]"
            />
          </div>
          <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              Password
            </label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full h-11 rounded-md border border-zinc-300 bg-white px-3 font-sans text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]"
            />
          </div>
        </div>

        <button
          type="submit"
          data-testid="login-submit"
          disabled={busy || !email.trim() || !password}
          className="mt-6 w-full h-11 rounded-md bg-[#002FA7] font-sans text-sm font-semibold text-white hover:bg-[#00258A] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 inline-flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {busy ? "Signing in..." : "Sign in"}
        </button>
        </form>
      </div>
    </div>
  );
}
